import React, { useState, useRef } from 'react';
import {
  Container, Box, Paper, Typography, TextField,
  Button, IconButton, InputAdornment, Fade
} from '@mui/material';
import { ArrowBack, MailOutline, Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import '../styles/styles.css';

const PinInput = ({ value, onChange }) => {
  const inputs = useRef([]);
  const digits = value.split('');

  const handleChange = (i, e) => {
    const val = e.target.value.replace(/\D/g, '').slice(-1);
    const next = digits.slice();
    next[i] = val;
    onChange(next.join(''));
    if (val && i < 5) inputs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    onChange(pasted.padEnd(6, '').slice(0, 6));
    inputs.current[Math.min(pasted.length, 5)]?.focus();
    e.preventDefault();
  };

  return (
    <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'center', my: 2 }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (inputs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digits[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          style={{
            width: 44,
            height: 52,
            textAlign: 'center',
            fontSize: '1.4rem',
            fontWeight: 600,
            border: '1.5px solid #c7c7cc',
            borderRadius: 10,
            outline: 'none',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
            color: '#1d1d1f',
            background: '#ffffff',
            transition: 'border-color 0.15s',
          }}
          onFocus={(e) => (e.target.style.borderColor = '#0071e3')}
          onBlur={(e) => (e.target.style.borderColor = '#c7c7cc')}
        />
      ))}
    </Box>
  );
};

const ForgotPassword = ({ notificationsRef }) => {
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('email');
  const navigate = useNavigate();

  const handleSendPin = async (e) => {
    e.preventDefault();
    if (!email) { notificationsRef.current.showSnackbar('Enter your email.', 'error'); return; }
    setLoading(true);
    try {
      await api.forgotPassword({ email });
      setStep('pin');
    } catch (err) {
      notificationsRef.current.showSnackbar(err.message, 'error');
    }
    setLoading(false);
  };

  const handleValidatePin = (e) => {
    e.preventDefault();
    if (pin.length !== 6) { notificationsRef.current.showSnackbar('Enter the 6-digit PIN.', 'error'); return; }
    setStep('password');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword) { notificationsRef.current.showSnackbar('Enter a new password.', 'error'); return; }
    if (newPassword !== confirmPassword) { notificationsRef.current.showSnackbar("Passwords don't match.", 'error'); return; }
    setLoading(true);
    try {
      await api.resetPassword({ pin, new_password: newPassword });
      notificationsRef.current.showSnackbar('Password updated!', 'success');
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      notificationsRef.current.showSnackbar(err.message, 'error');
      setLoading(false);
    }
  };

  return (
    <Box className="resetContainer">
      <IconButton onClick={() => navigate(-1)} className="resetBackButton" color="primary">
        <ArrowBack />
      </IconButton>
      <Container component="main" maxWidth="xs" className="resetMainContainer">
        <Fade in={true} timeout={600}>
          <Paper elevation={0} className="resetPaper">

            {step === 'email' && (
              <>
                <Box className="safeHeader" style={{ marginBottom: 24 }}>
                  <Box sx={{
                    width: 56, height: 56, borderRadius: '16px',
                    background: 'linear-gradient(135deg, #e8f0ff 0%, #dde8ff 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mb: 2, boxShadow: '0 2px 12px rgba(0, 113, 227, 0.12)'
                  }}>
                    <MailOutline sx={{ fontSize: 28, color: '#0071e3' }} />
                  </Box>
                  <Typography component="h1" className="resetTitle">Reset Password</Typography>
                  <Typography variant="body2" align="center" sx={{ fontSize: '0.82rem', color: '#8e8e93', letterSpacing: '0.2px', fontWeight: 400 }}>
                    We'll send a 6-digit PIN to your email
                  </Typography>
                </Box>
                <form onSubmit={handleSendPin} noValidate style={{ width: '100%' }}>
                  <TextField
                    required fullWidth margin="normal" label="Email"
                    type="email" autoComplete="email" autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button type="submit" fullWidth variant="contained" className="resetSubmitButton" disabled={loading}>
                    {loading ? 'Sending...' : 'Send PIN'}
                  </Button>
                </form>
              </>
            )}

            {step === 'pin' && (
              <>
                <Box className="safeHeader" style={{ marginBottom: 8 }}>
                  <Typography component="h1" className="resetTitle">Check your email</Typography>
                  <Typography variant="body2" align="center" sx={{ fontSize: '0.82rem', color: '#8e8e93', letterSpacing: '0.2px', fontWeight: 400 }}>
                    Enter the 6-digit PIN sent to <strong>{email}</strong>
                  </Typography>
                </Box>
                <form onSubmit={handleValidatePin} noValidate style={{ width: '100%' }}>
                  <PinInput value={pin} onChange={setPin} />
                  <Button type="submit" fullWidth variant="contained" className="resetSubmitButton">
                    Verify PIN
                  </Button>
                  <Button fullWidth onClick={() => { setPin(''); setStep('email'); }} sx={{ mt: 1, textTransform: 'none', fontWeight: 400, color: '#8e8e93' }}>
                    Resend PIN
                  </Button>
                </form>
              </>
            )}

            {step === 'password' && (
              <>
                <Box className="safeHeader" style={{ marginBottom: 24 }}>
                  <Typography component="h1" className="resetTitle">New Password</Typography>
                  <Typography variant="body2" align="center" sx={{ fontSize: '0.82rem', color: '#8e8e93', letterSpacing: '0.2px', fontWeight: 400 }}>
                    Choose a strong password
                  </Typography>
                </Box>
                <form onSubmit={handleResetPassword} noValidate style={{ width: '100%' }}>
                  <TextField
                    required fullWidth margin="normal" label="New Password"
                    type={showPassword ? 'text' : 'password'} autoFocus
                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(p => !p)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <TextField
                    required fullWidth margin="normal" label="Confirm Password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(p => !p)} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <Button type="submit" fullWidth variant="contained" className="resetSubmitButton" disabled={loading}>
                    {loading ? 'Updating...' : 'Update Password'}
                  </Button>
                </form>
              </>
            )}

          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default ForgotPassword;